import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { cropApi } from '@/services/cropApi';
import CaptureModal from '@/pages/devices/components/CaptureModal';
import Toast from '@/pages/devices/components/Toast';
import type { ToastMessage } from '@/pages/devices/components/Toast';

export default function CapturePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const urlDeviceId = searchParams.get('device_id');
  const storedDeviceId = localStorage.getItem('device_id');
  const deviceId = (urlDeviceId || storedDeviceId || '').trim();

  const [deviceName, setDeviceName] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [lastCapture, setLastCapture] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const toastIdRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const addToast = useCallback((text: string, type: ToastMessage['type']) => {
    toastIdRef.current += 1;
    const id = toastIdRef.current;
    setToasts((prev) => [...prev, { id, text, type }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const cleanupCaptureTimers = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  const fetchDeviceStatus = useCallback(async () => {
    if (!deviceId) return;
    try {
      const data = await cropApi.getSettings(deviceId);
      if (!mountedRef.current) return;
      if (data.result === 'success') {
        setDeviceName(data.device_name || '');
        setIsOnline(data.is_online === 'Y');
        setLastCapture(data.last_capture || '');
      }
    } catch {
      // silently ignore fetch errors
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [deviceId]);

  // 30s periodic device status refresh
  useEffect(() => {
    mountedRef.current = true;
    if (!deviceId) {
      setLoading(false);
      return;
    }

    fetchDeviceStatus();
    statusTimerRef.current = setInterval(fetchDeviceStatus, 30000);

    return () => {
      mountedRef.current = false;
      if (statusTimerRef.current) clearInterval(statusTimerRef.current);
      cleanupCaptureTimers();
    };
  }, [deviceId, fetchDeviceStatus, cleanupCaptureTimers]);

  const handleCaptureConfirm = useCallback(async () => {
    setShowModal(false);
    if (!deviceId || isCapturing) return;

    setIsCapturing(true);
    addToast('촬영 명령을 전송했습니다. 완료까지 약 1분 소요됩니다.', 'info');

    try {
      const res = await cropApi.sendCapture(deviceId);
      if (!mountedRef.current) return;

      if (res.result === 'success') {
        startCapturePolling();
      } else {
        setIsCapturing(false);
        cleanupCaptureTimers();
        addToast(res.message || '촬영 명령 전송 실패', 'error');
      }
    } catch {
      if (!mountedRef.current) return;
      setIsCapturing(false);
      cleanupCaptureTimers();
      addToast('촬영 명령 전송 중 오류가 발생했습니다.', 'error');
    }
  }, [deviceId, isCapturing, addToast, cleanupCaptureTimers]);

  const startCapturePolling = useCallback(() => {
    let pollCount = 0;
    const maxPoll = 36; // 5s * 36 = 180s = 3 minutes
    const pollInterval = 5000;

    cleanupCaptureTimers();

    pollTimerRef.current = setInterval(async () => {
      pollCount += 1;
      try {
        const data = await cropApi.getCaptureStatus(deviceId);
        if (!mountedRef.current) return;

        if (data.status === 'done') {
          cleanupCaptureTimers();
          setIsCapturing(false);
          const timeStr = data.capture_time || '';
          if (timeStr) setLastCapture(timeStr);
          addToast(`촬영 완료 (${timeStr.slice(11, 19)})`, 'success');
        } else if (data.status === 'fail') {
          cleanupCaptureTimers();
          setIsCapturing(false);
          addToast('촬영 실패', 'error');
        }
        // 'capturing' or 'none' → continue polling
      } catch {
        // polling error — continue
      }

      if (pollCount >= maxPoll) {
        cleanupCaptureTimers();
        setIsCapturing(false);
        addToast('촬영 결과를 확인할 수 없습니다. 장비 상태를 확인하세요.', 'warning');
      }
    }, pollInterval);
  }, [deviceId, addToast, cleanupCaptureTimers]);

  // Redirect if no device_id
  if (!deviceId && !loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-red-50">
            <i className="ri-error-warning-line text-2xl" style={{ color: '#e74c3c' }}></i>
          </div>
          <p className="text-foreground-700 font-medium mb-1">장비 ID를 찾을 수 없습니다</p>
          <p className="text-foreground-500 text-sm mb-4">
            URL에 device_id 파라미터를 추가하거나 먼저 대시보드에 접속해주세요.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2 rounded-lg text-sm font-medium text-white cursor-pointer whitespace-nowrap transition-all hover:brightness-110"
            style={{ backgroundColor: '#27ae60' }}
          >
            홈으로 이동
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Top bar */}
      <header className="w-full bg-white border-b border-background-200/70">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg text-foreground-700"></i>
            </button>
            <h1 className="text-foreground-950 font-bold text-sm md:text-base">수동 촬영</h1>
          </div>

          <button
            onClick={fetchDeviceStatus}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap border border-background-200/70"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-refresh-line"></i>
            </div>
            새로고침
          </button>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 md:px-6 py-6 md:py-10">
        <div className="max-w-lg mx-auto">
          {/* Device Card */}
          <div className="bg-white rounded-2xl border border-background-200/70 overflow-hidden">
            {/* Card body */}
            <div className="p-6 md:p-8">
              {/* Device Name */}
              <h2 className="text-xl font-bold text-foreground-950 mb-1">
                {loading ? '불러오는 중...' : (deviceName || '장비 정보')}
              </h2>

              {/* Device ID */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-cpu-line text-foreground-400 text-sm"></i>
                </div>
                <span className="text-xs text-foreground-500 font-mono">{deviceId}</span>
              </div>

              {/* Online Status Badge */}
              <div className="flex items-center gap-2 mb-5">
                {loading ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-500">
                    확인 중...
                  </span>
                ) : isOnline ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#27ae6018', color: '#27ae60' }}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: '#27ae60' }}></span>
                    온라인
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#95a5a618', color: '#95a5a6' }}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: '#95a5a6' }}></span>
                    오프라인
                  </span>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-background-200/70 mb-5"></div>

              {/* Last Capture */}
              <div className="mb-6">
                <p className="text-xs text-foreground-500 mb-1">마지막 촬영</p>
                <p className="text-sm font-medium text-foreground-800">
                  {loading ? '...' : (lastCapture || '-')}
                </p>
              </div>

              {/* Capture Button */}
              {isCapturing ? (
                <button
                  disabled
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-semibold cursor-not-allowed whitespace-nowrap transition-all"
                  style={{ backgroundColor: '#27ae6040', color: '#27ae60' }}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin"></i>
                  </div>
                  촬영 중...
                </button>
              ) : isOnline ? (
                <button
                  onClick={() => setShowModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-semibold text-white cursor-pointer whitespace-nowrap transition-all hover:brightness-110"
                  style={{ backgroundColor: '#27ae60' }}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-camera-line"></i>
                  </div>
                  촬영
                </button>
              ) : (
                <div className="relative group">
                  <button
                    disabled
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-semibold cursor-not-allowed whitespace-nowrap"
                    style={{ backgroundColor: '#d5dbdb', color: '#95a5a6' }}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-camera-line"></i>
                    </div>
                    촬영
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    장비가 오프라인입니다
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <CaptureModal
          deviceName={deviceName || deviceId}
          deviceId={deviceId}
          onCancel={() => setShowModal(false)}
          onConfirm={handleCaptureConfirm}
        />
      )}

      {/* Toast */}
      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
}